import {Routes} from '@angular/router';
import {unsavedChangesGuard} from "./core/guards/unsaved-changes-guard/unsaved-changes.guard";
import {MainLayoutComponent} from "./core/layouts/main-layout/main-layout.component";
import {registeredGuard} from "./core/guards/registered-guard/registered.guard";
import {MsalGuard} from "@azure/msal-angular";
import {environment} from "../environments/environment";
import {authorisedGuard} from "./core/guards/authorised-guard/authorised.guard";
import {MockLoginComponent} from "./feature/mock-login/mock-login.component";
import {LoginFailedComponent} from "./feature/login-failed/login-failed.component";
import {LogoutComponent} from "./feature/logout/logout.component";

export const routes: Routes = [
  {
    path: 'logout',
    component: LogoutComponent
  },
  {
    path: 'login-failed',
    component: LoginFailedComponent
  },
  {
    path: 'unauthorised',
    title: 'Unauthorised | SSBA',
    loadComponent: () => import('./feature/unauthorised/unauthorised.component')
      .then(component => component.UnauthorisedComponent)
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: environment.useMockAuth ? [authorisedGuard] : [MsalGuard, authorisedGuard],
    children: [
      {
        path: '',
        title: 'Home | SSBA',
        loadComponent: () => import("./feature/home/page/home.component")
          .then(component => component.HomeComponent),
      },
      {
        path: 'change-responsible-officer',
        title: 'Change of Responsible Officer | SSBA',
        loadComponent: () => import("./feature/change-responsible-officer/page/change-responsible-officer.component")
          .then(component => component.ChangeResponsibleOfficerComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'change-of-purpose',
        title: 'Change of Purpose | SSBA',
        loadComponent: () => import("./feature/change-of-purpose/page/change-of-purpose.component")
          .then(component => component.ChangeOfPurposeComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'start-to-handle-new-ssba',
        title: 'Start to Handle a New SSBA | SSBA',
        loadComponent: () => import("./feature/start-to-handle-new-ssba/page/start-to-handle-new-ssba.component")
          .then(component => component.StartToHandleNewSsbaComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'change-of-entity-and-facility-details',
        title: 'Change of Entity and Facility Details | SSBA',
        loadComponent: () => import(
          "./feature/change-of-entity-and-facility-details/page/change-of-entity-and-facility-details.component"
          ).then(component => component.ChangeOfEntityAndFacilityDetailsComponent),
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'application-for-extension',
        title: 'Application For Extension | SSBA',
        loadComponent: () => import("./feature/application-for-extension/page/application-for-extension.component")
          .then(component => component.ApplicationForExtensionComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'destruction-of-an-ssba',
        title: 'Destruction Of An SSBA | SSBA',
        loadComponent: () => import("./feature/destruction-of-an-ssba/page/destruction-of-an-ssba.component")
          .then(component => component.DestructionOfAnSsbaComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'transfers-in',
        title: 'Transfers In | SSBA',
        loadComponent: () => import("./feature/transfers-in/page/transfers-in.component")
          .then(component => component.TransfersInComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'transfers-out',
        title: 'Transfers Out | SSBA',
        loadComponent: () => import("./feature/transfers-out/page/transfers-out.component")
          .then(component => component.TransfersOutComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'temporary-handling',
        title: 'Temporary Handling | SSBA',
        loadComponent: () => import("./feature/temporary-handling/page/temporary-handling.component")
          .then(component => component.TemporaryHandlingComponent),
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'transfer-between-facilities-of-the-same-entity',
        title: 'Transfer Between Facilities of the Same Entity | SSBA',
        loadComponent: () => import("./feature/transfer-between-facilities-of-the-same-entity/page/transfer-between-facilities-of-the-same-entity.component")
          .then(component => component.TransferBetweenFacilitiesOfTheSameEntityComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'incident',
        title: 'Incident | SSBA',
        loadComponent: () => import("./feature/incident/page/incident.component")
          .then(component => component.IncidentComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'registration-of-a-linked-storage-unit',
        title: 'Registration of a Linked Storage Unit | SSBA',
        loadComponent: () => import("./feature/registration-of-a-linked-storage-unit/page/registration-of-a-linked-storage-unit.component")
          .then(component => component.RegistrationOfALinkedStorageUnitComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'removal-of-a-linked-storage-unit',
        title: 'Removal of a Linked Storage Unit | SSBA',
        loadComponent: () => import("./feature/removal-of-a-linked-storage-unit/page/removal-of-a-linked-storage-unit.component")
          .then(component => component.RemovalOfALinkedStorageUnitComponent),
        canActivate: [registeredGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'suspected-ssba-registered',
        title: 'Suspected SSBA | SSBA',
        loadComponent: () => import('./feature/suspected-ssba/registered/page/suspected-ssba-registered.component')
          .then(component => component.SuspectedSsbaRegisteredComponent),
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'suspected-ssba-non-registered',
        title: 'Suspected SSBA | SSBA',
        loadComponent: () => import('./feature/suspected-ssba/non-registered/page/suspected-ssba-non-registered.component')
          .then(component => component.SuspectedSsbaNonRegisteredComponent),
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'confirmation',
        title: 'Confirmation | SSBA',
        loadComponent: () => import('./feature/confirmation/page/confirmation.component')
          .then(component => component.ConfirmationComponent)
      },
      {
        path: 'form-submission-failed',
        title: 'Form Submission Failed | SSBA',
        loadComponent: () => import('./feature/form-submission-failed/page/form-submission-failed.component')
          .then(component => component.FormSubmissionFailedComponent)
      },
      {
        path: 'change-active-facility/:id',
        title: 'Changing Active Facility | SSBA',
        loadComponent: () => import('./feature/change-active-facility/change-active-facility.component')
          .then(component => component.ChangeActiveFacilityComponent)
      },
      {
        path: "**",
        title: 'SSBA',
        loadComponent: () => import("./feature/not-found/page/not-found.component")
          .then(component => component.NotFoundComponent)
      }
    ],
  },
]

export const mockRoutes = [
  {
    path: 'mock-login',
    title: 'Mock Login',
    component: MockLoginComponent
  }
];
